import React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { 
  ChevronDown, 
  ChevronUp, 
  ExternalLink,
  Calendar,
  DollarSign,
  Building2
} from 'lucide-react'
import { PartnerReferral } from '@/types/partner'
import { formatCurrency, formatDate } from '@/utils'

interface PartnerReferralsTableProps {
  referrals: PartnerReferral[]
  loading?: boolean
  onSort?: (field: string) => void
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  className?: string
}

const PartnerReferralsTable: React.FC<PartnerReferralsTableProps> = ({
  referrals,
  loading = false,
  onSort,
  sortField,
  sortDirection,
  className = ''
}) => {
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'default'
      case 'PENDING':
        return 'secondary'
      case 'REJECTED':
        return 'destructive'
      case 'CONTACTED':
        return 'outline'
      case 'CANCELLED':
        return 'destructive'
      case 'COMPLETED':
        return 'default'
      default:
        return 'outline'
    }
  }

  const getTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'INQUIRY':
        return 'default'
      case 'RFQ':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  const getReferralTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'BUY':
        return 'default'
      case 'SELL':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  const SortableHeader: React.FC<{
    field: string
    children: React.ReactNode
    className?: string
  }> = ({ field, children, className = '' }) => {
    const isSorted = sortField === field
    const isAsc = sortDirection === 'asc'

    return (
      <button
        onClick={() => onSort?.(field)}
        className={`flex items-center gap-1 font-medium text-left hover:text-primary transition-colors ${className}`}
      >
        {children}
        {isSorted ? (
          isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />
        ) : (
          <ChevronDown size={16} className="text-muted-foreground" />
        )}
      </button>
    )
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex items-center gap-4">
                <Skeleton className="w-10 h-10 rounded" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-6 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (referrals.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-12 text-center">
          <Building2 size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No referrals found</h3>
          <p className="text-muted-foreground">
            No referrals match your current filters. Try adjusting your search criteria.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="px-6 py-4 text-left">
                  <SortableHeader field="date">Date</SortableHeader>
                </th>
                <th className="px-6 py-4 text-left">Project</th>
                <th className="px-6 py-4 text-left">Type</th>
                <th className="px-6 py-4 text-left">Subtype</th>
                <th className="px-6 py-4 text-left">
                  <SortableHeader field="tranche_size">Tranche Size</SortableHeader>
                </th>
                <th className="px-6 py-4 text-left">
                  <SortableHeader field="valuation">Valuation</SortableHeader>
                </th>
                <th className="px-6 py-4 text-left">Status</th>
                <th className="px-6 py-4 text-left">API Key</th>
                <th className="px-6 py-4 text-left">Additional Info</th>
                <th className="px-6 py-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((referral) => (
                <tr key={referral.referral_id} className="border-b border-border hover:bg-accent/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} className="text-muted-foreground" />
                      <span className="text-sm">
                        {formatDate(referral.date)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {referral.project_logo ? (
                        <img
                          src={referral.project_logo}
                          alt={referral.project_name}
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                          }}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <Building2 size={16} className="text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{referral.project_name}</div>
                        <div className="text-sm text-muted-foreground">
                          ID: {referral.project_id.slice(0, 8)}...
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={getTypeBadgeVariant(referral.referral_type)}>
                      {referral.referral_type}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={getReferralTypeBadgeVariant(referral.referral_subtype)}>
                      {referral.referral_subtype}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <DollarSign size={16} className="text-muted-foreground" />
                      <span className="font-medium">
                        {formatCurrency(referral.tranche_size)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <DollarSign size={16} className="text-muted-foreground" />
                      <span className="font-medium">
                        {formatCurrency(referral.valuation)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={getStatusBadgeVariant(referral.status)}>
                      {referral.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium">{referral.key_name}</div>
                      <div className="text-muted-foreground">
                        {referral.key_id.slice(0, 8)}...
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      {referral.referral_type === 'INQUIRY' && (
                        <div className="space-y-1">
                          {referral.order_id && (
                            <div className="text-muted-foreground">
                              Order: {referral.order_id.slice(0, 8)}...
                            </div>
                          )}
                          {referral.order_valuation && (
                            <div className="text-muted-foreground">
                              Order Val: {formatCurrency(referral.order_valuation)}
                            </div>
                          )}
                          {referral.order_offered_amount && (
                            <div className="text-muted-foreground">
                              Offered: {formatCurrency(referral.order_offered_amount)}
                            </div>
                          )}
                        </div>
                      )}
                      {referral.referral_type === 'RFQ' && referral.terms && (
                        <div className="text-muted-foreground text-xs">
                          {referral.terms.length > 50 
                            ? `${referral.terms.slice(0, 50)}...` 
                            : referral.terms
                          }
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title="View details"
                    >
                      <ExternalLink size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

export default PartnerReferralsTable
